import { useState, useEffect, useCallback, useMemo } from 'react';
import { listGroups, getErrorMessage } from '../../api/groups';
import { useAuth } from '../../context/AuthContext';
import { getAppIcon } from './appIcons';
import { getAppColor } from './appColors';

const WEEKDAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const SESSION_LABELS = {
  initiation: 'Initiation',
  programmation: 'Programmation',
  simulation: 'Simulation',
  cloture: 'Clôture',
};

function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

// Vue calendrier mensuelle : chaque groupe apparaît comme une petite pastille
// colorée (par application) sur le jour de sa séance, avec navigation mois par mois.
export default function GroupsCalendar() {
  const { token } = useAuth();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cursor, setCursor] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [selectedGroup, setSelectedGroup] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    listGroups(token)
      .then(setGroups)
      .catch((err) => setError(getErrorMessage(err, 'Impossible de charger les groupes.')))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const today = new Date();

  const weeks = useMemo(() => {
    const firstOfMonth = cursor;
    const firstWeekday = (firstOfMonth.getDay() + 6) % 7; // lundi = 0
    const gridStart = new Date(firstOfMonth);
    gridStart.setDate(gridStart.getDate() - firstWeekday);

    const days = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(gridStart);
      d.setDate(gridStart.getDate() + i);
      days.push(d);
    }
    const result = [];
    for (let i = 0; i < 6; i++) result.push(days.slice(i * 7, i * 7 + 7));
    return result;
  }, [cursor]);

  const groupsByDay = (day) =>
    groups.filter((g) => g.session_date && sameDay(new Date(g.session_date), day));

  const monthLabel = cursor.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });

  const goPrev = () => setCursor((c) => new Date(c.getFullYear(), c.getMonth() - 1, 1));
  const goNext = () => setCursor((c) => new Date(c.getFullYear(), c.getMonth() + 1, 1));
  const goToday = () => setCursor(new Date(today.getFullYear(), today.getMonth(), 1));

  if (loading) return <p className="admin-empty">Chargement…</p>;
  if (error) return <div className="auth-error">{error}</div>;

  return (
    <div className="admin-card admin-card-wide">
      <div className="calendar-header">
        <h2 style={{ textTransform: 'capitalize', margin: 0 }}>{monthLabel}</h2>
        <div className="calendar-nav">
          <button className="calendar-nav-btn" onClick={goPrev}>‹</button>
          <button className="calendar-nav-btn calendar-today-btn" onClick={goToday}>Aujourd'hui</button>
          <button className="calendar-nav-btn" onClick={goNext}>›</button>
        </div>
      </div>

      <div className="calendar-grid calendar-weekdays">
        {WEEKDAYS.map((w) => <div key={w} className="calendar-weekday">{w}</div>)}
      </div>

      {weeks.map((week, wi) => (
        <div key={wi} className="calendar-grid">
          {week.map((day, di) => {
            const isCurrentMonth = day.getMonth() === cursor.getMonth();
            const isToday = sameDay(day, today);
            const dayGroups = groupsByDay(day);
            return (
              <div
                key={di}
                className={
                  'calendar-cell' +
                  (isCurrentMonth ? '' : ' calendar-cell-muted') +
                  (isToday ? ' calendar-cell-today' : '')
                }
              >
                <div className="calendar-cell-number">{day.getDate()}</div>
                <div className="calendar-cell-events">
                  {dayGroups.map((g) => (
                    <button
                      key={g.id}
                      className="calendar-event"
                      style={{ background: getAppColor(g.application) }}
                      onClick={() => setSelectedGroup(g)}
                      title={`${g.name} — ${SESSION_LABELS[g.session_type]} — ${g.progress}%`}
                    >
                      {getAppIcon(g.application)} {g.name}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ))}

      {selectedGroup && (
        <div className="calendar-popover-backdrop" onClick={() => setSelectedGroup(null)}>
          <div className="calendar-popover" onClick={(e) => e.stopPropagation()}>
            <button className="calendar-popover-close" onClick={() => setSelectedGroup(null)}>×</button>
            <div className="calendar-popover-title">
              {getAppIcon(selectedGroup.application)} {selectedGroup.name}
            </div>
            <div className="calendar-popover-line">{selectedGroup.application_label}</div>
            <div className="calendar-popover-line">
              📅 {new Date(selectedGroup.session_date).toLocaleDateString('fr-FR')}
            </div>
            <div className="calendar-popover-line">
              Séance : {SESSION_LABELS[selectedGroup.session_type]}
            </div>
            <div className="kanban-progress-bar" style={{ marginTop: 10 }}>
              <div className="kanban-progress-fill" style={{ width: `${selectedGroup.progress}%` }} />
            </div>
            <div className="calendar-popover-line">{selectedGroup.progress}% de progression</div>
            {selectedGroup.members.length > 0 && (
              <div className="calendar-popover-line">👤 {selectedGroup.members.join(', ')}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}